import * as Sentry from '@sentry/react';

// For now we hard-code sentry DSN here (it's not entirely private anyway).
// Longer term, we are moving away from sentry.
const SENTRY_CONFIG_DEFAULT = {
  dsn: 'https://e379c3ebac21863da885842a3fba55ff@o4509646470119424.ingest.us.sentry.io/4509646471233536',
  environment: 'production',
};

export function shouldInitSentry() {
  // Initalize sentry, except for local dev work, which throws errors as we write code.
  return !!window && !window.location.origin.includes('localhost');
}

export function maybeInitSentry(unmask?: boolean) {
  if (shouldInitSentry()) {
    initSentry(!!unmask);
  }
}

export function initSentry(unmask?: boolean) {
  // For local development, use environment variables if available
  // Fall back to server-injected configuration for production
  const serverConfig = (window as any).SENTRY_CONFIG || SENTRY_CONFIG_DEFAULT;

  // Check if we have environment variables (local development)
  const envDsn = (globalThis as any).__SENTRY_DSN__;
  const envEnvironment = (globalThis as any).__SENTRY_ENV__;

  const config = {
    dsn: envDsn || serverConfig?.dsn,
    environment: envEnvironment || serverConfig?.environment || 'development',
    tracesSampleRate: serverConfig?.tracesSampleRate ?? 1.0,
    sendDefaultPii: serverConfig?.sendDefaultPii ?? true,
    tracePropagationTargets: serverConfig?.tracePropagationTargets || ['localhost'],
    replaysSessionSampleRate: serverConfig?.replaysSessionSampleRate ?? 0.5,
    replaysOnErrorSampleRate: serverConfig?.replaysOnErrorSampleRate ?? 1.0,
    sampleRate: serverConfig?.sampleRate ?? 1.0,
  };

  if (!config.dsn) {
    console.warn('Sentry: no DSN configured, init aborted.');
    return;
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      tracesSampleRate: config.tracesSampleRate,
      sendDefaultPii: config.sendDefaultPii,
      tracePropagationTargets: config.tracePropagationTargets,
      replaysSessionSampleRate: unmask ? 1.0 : config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: unmask ? 1.0 : config.replaysOnErrorSampleRate,
      sampleRate: config.sampleRate,
      integrations: [
        Sentry.browserTracingIntegration({
          // Enable OpenTelemetry trace propagation
          shouldCreateSpanForRequest: url => {
            // Don't create spans for internal requests
            return !url.includes('/_sentry/');
          },
        }),
        Sentry.replayIntegration({
          networkDetailAllowUrls: unmask ? ['*'] : [],
          networkRequestHeaders: unmask ? ['*'] : [],
          networkResponseHeaders: unmask ? ['*'] : [],
          maskAllText: !unmask,
          maskAllInputs: !unmask,
          blockAllMedia: !unmask,
        }),
        Sentry.feedbackIntegration({
          autoInject: false,
          buttonLabel: 'Feedback',
          messagePlaceholder: 'Enter your message here...',
          showBranding: false,
          submitButtonLabel: 'Send Feedback',
          formTitle: 'Send Feedback',
          triggerLabel: '',
          useSentryUser: {
            name: 'username',
            email: 'email',
          },
        }),
      ],
      _experiments: {
        enableLogs: true,
      },
    });
  } catch (e) {
    console.error('Sentry initialization error: ', e);
  }
}
